import { useState } from 'react';
import {
  Copy,
  Check,
  Clock,
  CheckCircle,
  TrendingUp,
  Wallet,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import type { CampaignWithComputed } from '../types/campaign';
import type {
  InfluencerPaymentSummary,
  PaymentCategory,
  PaymentCategoryConfig,
} from '../types/payment';
import { PAYMENT_CATEGORIES, getPaymentCategoryAmount, getLastCreatorPaymentDate } from '../types/payment';
import { getInvoiceStats } from '../utils/paymentCalculations';
import { getParcelLabel } from '../utils/payableReadiness';
import { getCampaignReferenceDate } from '../utils/dates';
import { formatEuro } from '../utils/currency';
import { log } from '../lib/logger';
import { ExpandableRow, type RowTone } from './ui/ListRows';

// Amber: can be paid now; blue: waiting for the client; green: done
const CATEGORY_TONE: Record<PaymentCategory, RowTone> = { active: 'info', ready: 'warning', forecast: 'neutral', paid: 'success' };

interface InfluencerPaymentCardProps {
  summary: InfluencerPaymentSummary;
  onCampaignClick?: (campaign: CampaignWithComputed) => void;
  onMarkAsPaid?: (campaignId: string, amount: number, creatorName?: string) => Promise<void>;
}

const CategoryIcon = ({ category }: { category: PaymentCategory }) => {
  const iconClass = 'w-4 h-4';
  switch (category) {
    case 'active':
      return <Clock className={iconClass} />;
    case 'ready':
      return <CheckCircle className={iconClass} />;
    case 'forecast':
      return <TrendingUp className={iconClass} />;
    case 'paid':
      return <Wallet className={iconClass} />;
  }
};

interface CampaignTableProps {
  campaigns: CampaignWithComputed[];
  category: PaymentCategory;
  config: PaymentCategoryConfig;
  onCampaignClick?: (campaign: CampaignWithComputed) => void;
  onMarkAsPaid?: (campaignId: string, amount: number, creatorName?: string) => Promise<void>;
  creatorName?: string;
}

function CampaignTable({
  campaigns,
  category,
  config,
  onCampaignClick,
  onMarkAsPaid,
  creatorName,
}: CampaignTableProps) {
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);

  if (campaigns.length === 0) return null;

  const getMonthOfDeliverable = (campaign: CampaignWithComputed): string => {
    const referenceDate = getCampaignReferenceDate(campaign as { delivery_date?: string | null; event_date?: string | null });
    if (!referenceDate) return 'N/A';
    return new Date(referenceDate).toLocaleDateString('pt-PT', {
      year: 'numeric',
      month: 'short',
    });
  };

  const isOverdue = (campaign: CampaignWithComputed): boolean => {
    if (
      campaign.company_invoice_status === 'Issued' &&
      campaign.company_invoice_due_date
    ) {
      return new Date(campaign.company_invoice_due_date) < new Date();
    }
    return false;
  };

  const handleCheckboxClick = (e: React.MouseEvent, campaignId: string) => {
    e.stopPropagation();
    setConfirmingId(confirmingId === campaignId ? null : campaignId);
  };

  const handleConfirmPay = async (e: React.MouseEvent, campaign: CampaignWithComputed, amount: number) => {
    e.stopPropagation();
    if (!onMarkAsPaid || loadingId) return;
    setLoadingId(campaign.id);
    try {
      await onMarkAsPaid(campaign.id, amount, creatorName);
    } finally {
      setLoadingId(null);
      setConfirmingId(null);
    }
  };

  const handleCancelConfirm = (e: React.MouseEvent) => {
    e.stopPropagation();
    setConfirmingId(null);
  };

  const showPayColumn = category === 'ready' && !!onMarkAsPaid;

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className={config.bgColor}>
          <tr>
            {showPayColumn && (
              <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                Pago
              </th>
            )}
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Código
            </th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Marca
            </th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Ref. campanha
            </th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Mês
            </th>
            {category === 'active' && (
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Fatura ao cliente
              </th>
            )}
            {category === 'paid' && (
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Data de pagamento
              </th>
            )}
            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              {category === 'ready' ? 'Disponível' : 'Líquido criador'}
            </th>
            {category === 'ready' && (
              <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Parcelas prontas
              </th>
            )}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-100">
          {campaigns.map((campaign) => {
            const invoiceStats = category === 'ready' ? getInvoiceStats(campaign) : null;
            const isConfirming = confirmingId === campaign.id;
            const isLoading = loadingId === campaign.id;
            const payAmount = invoiceStats?.netPayable ?? campaign.net_fee_influencer;

            return (
              <tr
                key={campaign.id}
                className={`hover:bg-gray-50 cursor-pointer transition-colors ${isConfirming ? 'bg-green-50/50' : ''}`}
                onClick={() => onCampaignClick?.(campaign)}
              >
                {showPayColumn && (
                  <td className="px-3 py-2.5 whitespace-nowrap text-center" onClick={(e) => e.stopPropagation()}>
                    {isLoading ? (
                      <Loader2 className="w-4 h-4 mx-auto text-green-600 animate-spin" />
                    ) : isConfirming ? (
                      <div className="flex items-center gap-1 justify-center">
                        <button
                          onClick={(e) => handleConfirmPay(e, campaign, payAmount)}
                          className="px-1.5 py-0.5 text-xs font-medium bg-brand-ink text-white rounded hover:bg-green-700 transition-colors"
                        >
                          Yes
                        </button>
                        <button
                          onClick={handleCancelConfirm}
                          className="px-1.5 py-0.5 text-xs font-medium bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
                        >
                          No
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={(e) => handleCheckboxClick(e, campaign.id)}
                        className="w-5 h-5 rounded border-2 border-gray-300 hover:border-green-500 transition-colors flex items-center justify-center mx-auto group"
                        title={`Mark as paid: ${formatEuro(payAmount)}`}
                      >
                        <Check className="w-3 h-3 text-transparent group-hover:text-green-400 transition-colors" />
                      </button>
                    )}
                  </td>
                )}
                <td className="px-3 py-2.5 whitespace-nowrap text-sm text-gray-900">
                  {campaign.work_code || 'N/A'}
                </td>
                <td className="px-3 py-2.5 whitespace-nowrap text-sm text-gray-900">
                  {campaign.brand || campaign.client || 'N/A'}
                </td>
                <td className="px-3 py-2.5 whitespace-nowrap text-sm text-gray-600">
                  {campaign.campaign_ref || 'N/A'}
                </td>
                <td className="px-3 py-2.5 whitespace-nowrap text-sm text-gray-600">
                  {getMonthOfDeliverable(campaign)}
                </td>
                {category === 'active' && (
                  <td className="px-3 py-2.5 whitespace-nowrap text-sm">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                        isOverdue(campaign)
                          ? 'bg-red-100 text-red-800'
                          : campaign.company_invoice_status === 'Issued'
                            ? 'bg-blue-100 text-blue-800'
                            : campaign.company_invoice_status === 'Paid'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {isOverdue(campaign) && (
                        <AlertCircle className="w-3 h-3" />
                      )}
                      {isOverdue(campaign)
                        ? 'Vencida'
                        : ({ 'Not Issued': 'Por emitir', Issued: 'Emitida', Paid: 'Paga', Overdue: 'Vencida', Cancelled: 'Anulada' } as Record<string, string>)[campaign.company_invoice_status || ''] ?? campaign.company_invoice_status}
                    </span>
                  </td>
                )}
                {category === 'paid' && (
                  <td className="px-3 py-2.5 whitespace-nowrap text-sm text-gray-600">
                    {(() => {
                      const paidOn = getLastCreatorPaymentDate(campaign);
                      return paidOn ? new Date(paidOn).toLocaleDateString('pt-PT') : 'N/A';
                    })()}
                  </td>
                )}
                <td className="px-3 py-2.5 whitespace-nowrap text-sm font-medium text-right">
                  <span className={config.textColor}>
                    {formatEuro(getPaymentCategoryAmount(campaign, category))}
                  </span>
                </td>
                {category === 'ready' && invoiceStats && (
                  <td className="px-3 py-2.5 whitespace-nowrap text-sm text-center">
                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800">
                      {getParcelLabel(campaign, invoiceStats)}
                    </span>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function InfluencerPaymentCard({
  summary,
  onCampaignClick,
  onMarkAsPaid,
}: InfluencerPaymentCardProps) {
  const [expandedCategories, setExpandedCategories] = useState<
    Set<PaymentCategory>
  >(new Set(['ready']));
  const [copiedCategory, setCopiedCategory] = useState<PaymentCategory | null>(
    null
  );

  const toggleCategory = (category: PaymentCategory) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };

  const copyReadyPayments = async () => {
    const campaigns = summary.campaigns.ready;
    if (campaigns.length === 0) return;

    const padRight = (str: string, length: number) =>
      str + ' '.repeat(Math.max(0, length - str.length));

    const workCodeWidth = 15;
    const campaignRefWidth = 20;
    const brandWidth = 25;
    const monthWidth = 20;
    const netWidth = 15;
    const parcelWidth = 10;

    const header = `${padRight('Work Code', workCodeWidth)}${padRight('Campaign Ref', campaignRefWidth)}${padRight('Brand', brandWidth)}${padRight('Month', monthWidth)}${padRight('Amount Available', netWidth)}${padRight('Parcel', parcelWidth)}`;
    const separator = '-'.repeat(
      workCodeWidth + campaignRefWidth + brandWidth + monthWidth + netWidth + parcelWidth
    );

    const rows = campaigns
      .map((campaign) => {
        const invoiceStats = getInvoiceStats(campaign);
        const workCode = padRight(campaign.work_code || 'N/A', workCodeWidth);
        const campaignRef = padRight(
          (campaign.campaign_ref || 'N/A').substring(0, 19),
          campaignRefWidth
        );
        const brand = padRight(
          (campaign.brand || campaign.client || 'N/A').substring(0, 24),
          brandWidth
        );
        const month = padRight(
          getCampaignReferenceDate(campaign as { delivery_date?: string | null; event_date?: string | null })
            ? new Date(getCampaignReferenceDate(campaign as { delivery_date?: string | null; event_date?: string | null }) as string).toLocaleDateString('pt-PT', {
                year: 'numeric',
                month: 'short',
              })
            : 'N/A',
          monthWidth
        );
        const net = padRight(
          formatEuro(invoiceStats.netPayable),
          netWidth
        );
        const parcel = padRight(
          getParcelLabel(campaign, invoiceStats),
          parcelWidth
        );
        return `${workCode}${campaignRef}${brand}${month}${net}${parcel}`;
      })
      .join('\n');

    const totalLabel = padRight(
      'Total:',
      workCodeWidth + campaignRefWidth + brandWidth + monthWidth
    );
    const totalAmount = formatEuro(summary.totals.ready);
    const message = `${header}\n${separator}\n${rows}\n${separator}\n${totalLabel}${totalAmount}`;

    try {
      await navigator.clipboard.writeText(message);
      setCopiedCategory('ready');
      setTimeout(() => setCopiedCategory(null), 2000);
    } catch (error) {
      log.error('Failed to copy:', error);
    }
  };

  // A campaign paid in tranches sits in two categories; count it once
  const totalCampaigns = new Set(
    (['active', 'ready', 'forecast', 'paid'] as PaymentCategory[]).flatMap((cat) =>
      summary.campaigns[cat].map((c) => c.id)
    )
  ).size;

  const categories: PaymentCategory[] = ['active', 'ready', 'forecast', 'paid'];

  return (
    <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-100 overflow-hidden">
      <div className="bg-gradient-to-r from-slate-50 to-slate-100 px-5 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {summary.influencer}
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">
              {totalCampaigns} {totalCampaigns !== 1 ? 'campanhas' : 'campanha'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {summary.counts.ready > 0 && (
              <button
                onClick={copyReadyPayments}
                className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                  copiedCategory === 'ready'
                    ? 'bg-brand-ink text-white'
                    : 'bg-brand-ink text-white hover:bg-black'
                }`}
              >
                {copiedCategory === 'ready' ? (
                  <>
                    <Check className="w-3.5 h-3.5" />
                    Copiado!
                  </>
                ) : (
                  <>
                    <Copy className="w-3.5 h-3.5" />
                    Copiar prontas
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-4 gap-2 mt-4">
          {categories.map((category) => {
            const config = PAYMENT_CATEGORIES[category];
            const count = summary.counts[category];
            const total = summary.totals[category];

            return (
              <div
                key={category}
                className={`${config.bgColor} ${config.borderColor} border rounded-lg p-2 text-center`}
              >
                <div className={`text-xs font-medium ${config.textColor}`}>
                  {config.label}
                </div>
                <div className={`text-lg font-bold ${config.textColor}`}>
                  {count}
                </div>
                <div className={`text-xs ${config.textColor} opacity-75`}>
                  {formatEuro(total)}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="divide-y divide-gray-200">
        {categories.map((category) => {
          const config = PAYMENT_CATEGORIES[category];
          const campaigns = summary.campaigns[category];
          const isExpanded = expandedCategories.has(category);

          if (campaigns.length === 0) return null;

          return (
            <ExpandableRow
              key={category}
              tone={CATEGORY_TONE[category]}
              isExpanded={isExpanded}
              onToggle={() => toggleCategory(category)}
              summary={<>
                <span className={`p-1 rounded-md ${config.bgColor} ${config.textColor}`}>
                  <CategoryIcon category={category} />
                </span>
                <span className="text-sm font-semibold text-gray-900">{config.label}</span>
                <span className="text-xs text-gray-500">
                  {campaigns.length} {campaigns.length !== 1 ? 'campanhas' : 'campanha'}
                </span>
              </>}
              aside={<span className={config.textColor}>{formatEuro(summary.totals[category])}</span>}
            >
              <div className={`-ml-4 -mr-3 -mb-4 -mt-3 border-t ${config.borderColor} bg-white overflow-x-auto`}>
                <CampaignTable
                  campaigns={campaigns}
                  category={category}
                  config={config}
                  onCampaignClick={onCampaignClick}
                  onMarkAsPaid={onMarkAsPaid}
                  creatorName={summary.influencer}
                />
              </div>
            </ExpandableRow>
          );
        })}
      </div>
    </div>
  );
}
